// @ts-check

/**
 * GeoJSON data processing utilities.
 * Filters, transforms, and enriches GeoJSON features for map layers.
 * This is dependent on the structure of the geojson and content csv data. This is subject to change if either of those data structures change.
 */

/**
 * Filter GeoJSON features to only Point geometries (islands).
 * @param {Object} geoJson - Full GeoJSON FeatureCollection
 * @returns {Object} GeoJSON FeatureCollection with only Point features
 */
export function filterIslandFeatures(geoJson) {
  const islandFeatures = geoJson.features.filter(
    (feature) => feature.geometry.type === "Point"
  );

  return {
    type: "FeatureCollection",
    features: islandFeatures
  };
}

/**
 * Filter GeoJSON features to only cultural region polygons (Polygon/MultiPolygon with layer_id === "cultural").
 * @param {Object} geoJson - Full GeoJSON FeatureCollection
 * @returns {Object} GeoJSON FeatureCollection with only cultural region Polygon features
 */
export function filterRegionFeatures(geoJson) {
  const regionFeatures = geoJson.features.filter(
    (feature) =>
      (feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon") &&
      feature.properties?.layer_id === "cultural"
  );

  return {
    type: "FeatureCollection",
    features: regionFeatures
  };
}

/**
 * Enrich GeoJSON features with CSV data by matching mapbox_id.
 * @param {Object} geoJson - GeoJSON FeatureCollection to enrich
 * @param {Array} csvData - Array of CSV records
 * @param {string} csvIdField - CSV field name containing the ID
 * @param {string} [featureType] - Optional type field to add to properties
 * @returns {Object} Enriched GeoJSON FeatureCollection
 */
export function enrichFeaturesWithCsvData(
  geoJson,
  csvData,
  csvIdField,
  featureType
) {
  const enrichedFeatures = geoJson.features.map((feature) => {
    const csvRecord = csvData.find(
      (record) => record[csvIdField] === feature.properties.mapbox_id
    );

    return {
      ...feature,
      properties: {
        ...feature.properties,
        ...(csvRecord || {}),
        ...(featureType && { type: featureType })
      }
    };
  });

  return {
    type: "FeatureCollection",
    features: enrichedFeatures
  };
}

/**
 * Create GeoJSON FeatureCollection from migration routes data.
 * @param {Array} migrationRoutes - Array of migration route records
 * @returns {Object} GeoJSON FeatureCollection
 */
export function createMigrationRoutesGeoJson(migrationRoutes) {
  const features = migrationRoutes.map((route) => ({
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: route.coordinates || []
    },
    properties: {
      id: route.id,
      name: route.description,
      type: "route",
      ...route
    }
  }));

  return {
    type: "FeatureCollection",
    features
  };
}

/**
 * Match island feature with CSV data and merge properties.
 * @param {Object} feature - GeoJSON feature
 * @param {Array} islands - Array of island CSV records
 * @param {string} islandIdField - CSV field name for island ID
 * @returns {Object} Merged island data object
 */
export function matchIslandWithCsvData(feature, islands, islandIdField) {
  const csvIsland = islands.find(
    (island) => island[islandIdField] === feature.properties.mapbox_id
  );

  return csvIsland
    ? { ...feature.properties, ...csvIsland, type: "island" }
    : { ...feature.properties, type: "island" };
}

/**
 * Match migration wave polygon feature with CSV data and merge properties.
 * @param {Object} feature - GeoJSON feature
 * @param {Array} migrationRoutes - Array of migration route CSV records
 * @param {string} routeIdField - CSV field name for route ID (e.g., "Route ID")
 * @returns {Object} Merged migration route data object
 */
export function matchMigrationWavePolygonWithCsvData(
  feature,
  migrationRoutes,
  routeIdField
) {
  const csvRoute = migrationRoutes.find(
    (route) => route[routeIdField] === feature.properties.mapbox_id
  );

  return csvRoute
    ? { ...feature.properties, ...csvRoute, type: "route" }
    : { ...feature.properties, type: "route" };
}
